import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowDown } from "lucide-react";
import img_gekkins from "@/assets/gekkins_v4.png";
import demo1 from "@/assets/videos/demo_overview.mp4";
import demo2 from "@/assets/videos/demo_addnode.mp4";
import demo3 from "@/assets/videos/demo_autoescalado.mp4";

function RevealSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, { once: false, margin: "-12% 0px -12% 0px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.88, y: 18 }}
      animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ type: "spring", stiffness: 700, damping: 23, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function LazyAutoPlayVideo({
  src,
  wrapperClassName = "",
  videoClassName = "",
}: {
  src: string;
  wrapperClassName?: string;
  videoClassName?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    once: false,
    margin: "-12% 0px -12% 0px",
  });

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden rounded-xl shadow-[0_8px_20px_rgba(0,0,0,0.1)] w-full ${wrapperClassName}`}
    >
      {/* Reserve space to avoid layout shift */}
      <div className="w-full">
        <video
          src={src}
          muted
          playsInline
          className="w-full h-auto block opacity-0 pointer-events-none"
        />
      </div>

      {/* Real video */}
      {inView && (
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className={`absolute inset-0 w-full h-full object-cover ${videoClassName}`}
        />
      )}
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-2 h-2 w-2 rounded-full bg-lime-500 inline-block shrink-0" />
      <span>{children}</span>
    </li>
  );
}

export default function Home({ loaderVisible }: { loaderVisible: boolean }) {
  return (
    <motion.div
      key="content"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ delay: loaderVisible ? 1.9 : 0 }}
    >
      <motion.main
        initial={{ scale: 0.88, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 700,
          damping: 23,
          delay: loaderVisible ? 2 : 0,
        }}
        className="page_centered_2 pt-24 sm:pt-20 pb-28 px-4 sm:px-6 lg:px-10"
      >
        <div className="w-full max-w-7xl flex flex-col mt-17 gap-20 sm:gap-24 lg:gap-32">
          {/* Hero */}
          <RevealSection>
            <section className="section_header mt-12 gap-10 flex flex-col">
              <div className="flex items-center justify-center gap-5 sm:gap-7">
                <img
                  src={img_gekkins}
                  alt="Gekkins"
                  className="h-24 w-24 sm:h-40 sm:w-40 lg:h-45 lg:w-45 object-contain"
                />

                <h1 className="font_1 leading-none sm:text-[8rem]! lg:text-[10rem]!">
                  Gekkins
                </h1>
              </div>

              <h3 className="font_1 leading-[0.95] text-center">
                Connect servers. Deploy apps. Done.
              </h3>

              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 w-full max-w-5xl mx-auto px-6 lg:px-10">
                <div className="lg:w-1/2 w-full flex justify-center lg:justify-end">
                  <div className="w-full max-w-sm lg:max-w-lg">
                    <LazyAutoPlayVideo src={demo1} />
                  </div>
                </div>

                <div className="lg:w-1/2 w-full flex flex-col gap-4 text-center lg:text-left max-w-md mx-auto lg:mx-0">
                  <p className="font_2 text-black/60 leading-relaxed">
                    Modern infrastructure has become unnecessarily complex.
                    Containers, orchestration, networking, deployment flows and
                    endless configuration often get in the way of actually
                    shipping software.
                  </p>

                  <p className="font_2 text-black/60 leading-relaxed">
                    GEKKINS rethinks that experience from the ground up. Connect
                    your servers, deploy your application, and let
                    GEKKINS handle the infrastructure behind the scenes.
                  </p>

                  <Link
                    to="/register"
                    className="btn_si mt-2 self-center px-20!"
                  >
                    Join the waitlist
                  </Link>

                </div>
              </div>

              <ArrowDown className="mt-8 hidden lg:block" />
            </section>
          </RevealSection>

          {/* Demo 2 */}
          <RevealSection delay={0.07}>
            <div id="demo" className="flex flex-col gap-6">
              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="flex flex-col gap-6 lg:w-1/3">
                  <h2 className="font_1 leading-tight">
                    Turn servers into a cluster in seconds
                  </h2>

                  <p className="font_2 text-black/60 leading-relaxed text-sm">
                    Add remote nodes through SSH and start managing them from a
                    single visual interface.
                  </p>

                  <ul className="font_2 text-black/55 flex flex-col gap-2 text-sm">
                    <Bullet>Add remote nodes in seconds.</Bullet>
                    <Bullet>No prior cluster setup needed.</Bullet>
                    <Bullet>One place to control everything.</Bullet>
                  </ul>
                </div>

                <div className="lg:w-2/3 w-full">
                  <LazyAutoPlayVideo src={demo2} />
                </div>
              </div>
            </div>
          </RevealSection>

          {/* Demo 3 */}
          <RevealSection delay={0.07}>
            <div className="flex flex-col gap-6 mt-8">
              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="lg:w-2/3 w-full">
                  <LazyAutoPlayVideo src={demo3} />
                </div>

                <div className="flex flex-col gap-6 lg:w-1/3">
                  <h2 className="font_1 leading-tight">
                    Deploy distributed applications without the complexity
                  </h2>

                  <p className="font_2 text-black/60 leading-relaxed text-sm">
                    Manage services, monitor your infrastructure and keep your
                    stack moving fast from a single interface designed to feel
                    clear from the first minute.
                  </p>

                  <ul className="font_2 text-black/55 flex flex-col gap-2 text-sm">
                    <Bullet>Autoscale services when demand changes.</Bullet>
                    <Bullet>Monitor cluster status in real time.</Bullet>
                    <Bullet>Less terminal. More control.</Bullet>
                  </ul>
                </div>
              </div>
            </div>
          </RevealSection>

          {/* CTA */}
          <RevealSection delay={0.07}>
            <section id="waitlist" className="section_header py-8 sm:py-12">
              <h2 className="font_1 font-black leading-[0.92] text-black max-w-3xl">
                Building the tool we wish we had.
              </h2>

              <p className="font_2 text-black/55 max-w-xl leading-relaxed">
                Join the waitlist to follow the project&apos;s progress and try
                early versions as they land.
              </p>

              <div className="w-full max-w-sm flex flex-col gap-3">
                <Link to="/register" className="btn_si w-full px-5 py-3">
                  Join the waitlist
                </Link>
              </div>
            </section>
          </RevealSection>
        </div>
      </motion.main>

      {/* Footer */}
      <footer className="flex flex-col items-center mt-5 gap-2 py-5 border-t border-black/8">
        <div className="flex items-center gap-1">
          <img
            src={img_gekkins}
            alt="Gekkins"
            className="h-5 w-5 object-contain opacity-60"
          />
          <span className="font_1 text-black/40 text-sm font-bold">Gekkins</span>
        </div>

        <p className="text-black/30">
          © {new Date().getFullYear()} GEKKINS by Guillermo Álvarez Franganillo.
        </p>
      </footer>
    </motion.div>
  );
}
